import React, { useCallback, useEffect, useState } from 'react';
import {
  IconButton, Badge, Menu, MenuItem, Typography, Box, Divider, Button, ListItemText,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from '../../utils/axios';
import { subscribeToDataSync } from '../../utils/dataSync';
import { useVisibleInterval } from '../../hooks/useVisibleInterval';
import { useAuth } from '../../context/AuthContext';
import { resolveSelfServiceDeepLink } from '../../config/selfServiceAccess';
import { shouldOpenInNewTab } from '../../utils/navClick';

const RT = { primary: '#09b8a9', textSec: '#9e9d9d' };
const UNREAD_POLL_MS = 120000; // badge poll — full list loads on menu open

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    try {
      const countRes = await axios.get('/notifications/unread-count');
      setUnreadCount(countRes.data?.data?.count || 0);
    } catch {
      /* ignore */
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        axios.get('/notifications', { params: { limit: 15 } }),
        axios.get('/notifications/unread-count'),
      ]);
      setNotifications(listRes.data?.data?.notifications || []);
      setUnreadCount(countRes.data?.data?.count || 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadUnreadCount();
    const unsubscribe = subscribeToDataSync(() => loadUnreadCount());
    return unsubscribe;
  }, [loadUnreadCount]);

  useVisibleInterval(loadUnreadCount, UNREAD_POLL_MS);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    loadNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const markReadOnly = async (id) => {
    try {
      await axios.patch(`/notifications/${id}/read`);
    } catch {
      /* ignore */
    }
    loadNotifications();
  };

  const openNotification = async (event, n, href) => {
    await markReadOnly(n._id);
    if (!href) {
      handleClose();
      return;
    }
    if (shouldOpenInNewTab(event)) {
      // Browser / RouterLink handles new tab; just close the menu
      handleClose();
      return;
    }
    event.preventDefault();
    handleClose();
    navigate(href);
  };

  const markAllRead = async () => {
    try {
      await axios.patch('/notifications/read-all');
      loadNotifications();
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <IconButton onClick={handleOpen} aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`} sx={{ color: '#383b50', mr: 0.5 }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 'min(360px, calc(100vw - 24px))', maxHeight: 420 } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={700}>Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllRead} sx={{ textTransform: 'none', color: RT.primary }}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="No notifications" secondary="You're all caught up" />
          </MenuItem>
        ) : (
          notifications.map((n) => {
            const href = n.link ? resolveSelfServiceDeepLink(n.link, user?.role) : null;
            return (
              <MenuItem
                key={n._id}
                component={href ? RouterLink : 'li'}
                to={href || undefined}
                onClick={(e) => openNotification(e, n, href)}
                sx={{ bgcolor: n.isRead ? 'inherit' : '#f0faf9', alignItems: 'flex-start', whiteSpace: 'normal' }}
              >
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={n.isRead ? 400 : 700}>{n.title}</Typography>}
                  secondary={
                    <>
                      <Typography variant="caption" display="block" color={RT.textSec}>{n.message}</Typography>
                      <Typography variant="caption" color={RT.textSec}>
                        {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                      </Typography>
                    </>
                  }
                />
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
}
